// The hub's Hub token as its curator reads it on Agent activity: whether the
// hub agent can work, in words, and the change the curator makes next. The
// hub agent holds no token: it acts as the curator who connected the Hub
// token, with a short-lived child of it, and never activates or lists. Studio
// follows the Curator API's state and keeps no threshold of its own: Expiring
// is the Exchange's. See Design > Hub agent, "The credential path", and
// Design > UI, "Studio hierarchy".

import type { HubSecretState } from "../../../src/exchange/curator-api.ts";

/** What the panel shows: a badge, a line, the button that connects (`connect`), and whether Disconnect is offered. */
export type HubTokenView = {
  label: string; tone: "success" | "warning" | "danger" | "neutral"; text: string; connect: string | null; connected: boolean;
};

const NOT_CONNECTED: HubTokenView = {
  label: "Not connected", tone: "neutral", connected: false, connect: "Connect",
  text: "Your hub agent works with your WarmHub access. Connect it, and it reads this hub's repository, drafts the questions it will answer for you to review, then builds, tests and stages a toolset. It can't activate or list your hub: you do.",
};
// A state that asks the curator to connect again says why, and the agent's work stops until they do.
const again = (label: string, tone: HubTokenView["tone"], why: string): HubTokenView =>
  ({ label, tone, connected: true, connect: "Connect again", text: `${why} Your hub agent can't work until you connect again.` });
const VIEWS: Record<HubSecretState["state"], (until: string | null) => HubTokenView> = {
  Live: (until) => ({ label: "Connected", tone: "success", connected: true, connect: null,
    text: `Your hub agent works with your WarmHub access${until ? ` until ${until}` : ""}. It reads this hub only, and can't activate or list it.` }),
  Expiring: (until) => ({ label: until ? `Expires ${until}` : "Expiring", tone: "warning", connected: true, connect: "Connect again",
    text: `Your hub agent's access expires ${until ?? "soon"}. Connect again before then to keep it working.` }),
  Expired: () => again("Expired", "danger", "Your hub agent's access expired."),
  Refused: () => again("Refused", "danger", "WarmHub refused your hub agent's access. Check that your account can still configure this hub's repository."),
  // The Exchange reports Missing for a state it couldn't read; connecting again stores a fresh one.
  Missing: () => NOT_CONNECTED,
};

/** The Hub token's view, with its expiry in words by `day`; `null` is a hub no curator has connected. */
export const hubTokenView = (secret: HubSecretState | null, day: (ms: number) => string): HubTokenView =>
  secret === null ? NOT_CONNECTED : VIEWS[secret.state](secret.expiresAt === null ? null : day(secret.expiresAt));
